import { useEffect, useMemo } from 'react';
import { Box } from '@nimbus-ds/components';
import { Page } from '@nimbus-ds/patterns';
import { useLocation } from 'react-router';
import { useNavegate } from 'App/hooks';
import { HeaderTop, IonPageStratus } from 'commons/components';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { DocumentType } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import { useGetInformationForDocument } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/hooks';
import useAsyncLabelPollingOrchestrator from 'domains/NuvemEnvioCommons/Orders/hooks/useAsyncLabelPollingOrchestrator';
import { getQueryParams } from 'domains/NuvemEnvioCommons/Orders/utils';
import getReturnPrintLabelUrl from 'domains/NuvemEnvioCommons/Orders/utils/getReturnPrintLabelUrl';
import {
  trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsSuccess,
  trackingNuvemEnvioPrintDocumentsGeneratedErrorLabels,
} from 'domains/NuvemEnvioCommons/tracking';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  PdfPage,
  PrintLabelHeader,
  PrintLabelResult,
  ProgressCard,
} from './components';
import OrdersSafariAlert from '../../components/SafariAlert';
import useNavigatorInfo from '../../hooks/useNavigatorInfo/useNavigatorInfo';
import { ProgressCardType } from '../constants';

function OrdersAsyncLabelGenerateStatusPage(): JSX.Element {
  const { goBack } = useNavegate();
  const { isSafari } = useNavigatorInfo();
  const { isPostPaid, status: nuvemEnvioConfigsStatus } =
    useGetNuvemEnvioConfigs();
  const { fetchInformationForDocument, information } =
    useGetInformationForDocument(DocumentType.NUVEM_ENVIO_LABEL);
  const isMobile = useIsMobileDevice();

  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const { ids, source, orderId } = getQueryParams(searchParams);
  const pollingOrchestrator = useAsyncLabelPollingOrchestrator({ orderId });

  const {
    isSimulationPolling,
    isConcatenationPolling,
    onProgress,
    hasError,
    hasSuccess,
    progressInPercentage,
    totalOrders,
    generatedOrders,
    readyLabels,
    erroredLabels,
    concatenationDocumentUrl,
    downloadDocumentUrl,
    combinedErrorCodes,
    reset,
    initialize,
  } = pollingOrchestrator;

  useEffect(() => {
    if (!ids.length) return;

    if (nuvemEnvioConfigsStatus.isSuccess) {
      fetchInformationForDocument({ fulfillmentOrders: ids });
      initialize(ids, orderId);
    }

    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuvemEnvioConfigsStatus.isSuccess]);

  useEffect(() => {
    if (hasSuccess) {
      trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsSuccess(orderId);
    }
  }, [hasSuccess, orderId]);

  useEffect(() => {
    if (hasError) {
      trackingNuvemEnvioPrintDocumentsGeneratedErrorLabels(
        orderId,
        combinedErrorCodes,
      );
    }
  }, [hasError, orderId, combinedErrorCodes]);

  return (
    <IonPageStratus
      width="small"
      headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
    >
      <Page id="NuvemEnvioAsyncLabelGenerateStatusPage" padding="none">
        <PrintLabelHeader information={information} />
        <Page.Body id="NuvemEnvioAsyncLabelGenerateStatusPageBody">
          {onProgress ? (
            <ProgressCard
              progressPercentage={progressInPercentage}
              totalOrders={totalOrders}
              generatedOrders={generatedOrders}
              type={
                !isPostPaid && isSimulationPolling
                  ? ProgressCardType.BILLING_SIMULATION
                  : ProgressCardType.SEARCH
              }
            />
          ) : (
            <Box display="flex" flexDirection="column" gap="4">
              {hasSuccess && (
                <Box display="flex" flexDirection="column" gap="4">
                  {isSafari && <OrdersSafariAlert />}
                  <PrintLabelResult
                    type="success"
                    labels={readyLabels}
                    orderId={orderId}
                    isConcatenationInProgress={isConcatenationPolling}
                    information={information}
                    returnPath={getReturnPrintLabelUrl(source, orderId)}
                  />
                </Box>
              )}
              {hasError && (
                <PrintLabelResult
                  type="error"
                  returnPath={getReturnPrintLabelUrl(source, orderId)}
                  information={information}
                  labels={readyLabels}
                  erroredLabels={erroredLabels}
                />
              )}
            </Box>
          )}
          {((!!downloadDocumentUrl && !isMobile) ||
            (!!concatenationDocumentUrl && !isMobile)) && (
            <PdfPage
              url={concatenationDocumentUrl || downloadDocumentUrl || ''}
            />
          )}
        </Page.Body>
      </Page>
    </IonPageStratus>
  );
}

export default OrdersAsyncLabelGenerateStatusPage;
